/**
 * Status constants to avoid magic strings throughout the codebase
 *
 * Makes code more maintainable and prevents typos
 */

// Project status values
export const PROJECT_STATUS = {
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  ARCHIVED: 'Archived',
} as const

export type ProjectStatus = (typeof PROJECT_STATUS)[keyof typeof PROJECT_STATUS]

// Agent status values (from backend enum)
export const AGENT_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
} as const

export type AgentStatus = (typeof AGENT_STATUS)[keyof typeof AGENT_STATUS]

/** True when agent is deployed (API `active` or UI label `Published`). */
export function isAgentDeployed(status: string | undefined | null): boolean {
  if (!status) return false
  const normalized = status.toLowerCase()
  return normalized === AGENT_STATUS.ACTIVE || normalized === 'published' || normalized === 'deployed'
}

// Agent registry status values (decoupled from agent status)
export const REGISTRY_STATUS = {
  UNREGISTERED: 'unregistered',
  PENDING: 'pending',
  REGISTERED: 'registered',
  ARCHIVED: 'archived',
} as const

export type RegistryStatus = (typeof REGISTRY_STATUS)[keyof typeof REGISTRY_STATUS]

// Model/Tool/Knowledge status values
export const RESOURCE_STATUS = {
  ACTIVE: 'Active',
  REVIEW: 'Review',
  DEPRECATED: 'Deprecated',
} as const

export type ResourceStatus = (typeof RESOURCE_STATUS)[keyof typeof RESOURCE_STATUS]

/** tools_registry.status values — must match backend ToolStatus enum */
export const TOOL_REGISTRY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ERROR: 'error',
  PENDING: 'pending',
} as const

export type ToolRegistryStatus = (typeof TOOL_REGISTRY_STATUS)[keyof typeof TOOL_REGISTRY_STATUS]

export function toolRegistryStatusLabel(value: string | null | undefined): string {
  const v = (value || '').toLowerCase()
  switch (v) {
    case 'active':
      return 'Active'
    case 'inactive':
      return 'Inactive'
    case 'error':
      return 'Error'
    case 'pending':
      return 'Pending'
    default:
      return value || 'Unknown'
  }
}
export const USER_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  REJECTED: 'rejected',
  DISABLED: 'disabled',
} as const

export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS]

// Simulation status values
export const SIMULATION_STATUS = {
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export type SimulationStatus = (typeof SIMULATION_STATUS)[keyof typeof SIMULATION_STATUS]

// Knowledge connector sync status
export const KNOWLEDGE_SYNC_STATUS = {
  SYNCED: 'Synced',
  SYNCING: 'Syncing',
  ERROR: 'Error',
} as const

export type KnowledgeSyncStatus = (typeof KNOWLEDGE_SYNC_STATUS)[keyof typeof KNOWLEDGE_SYNC_STATUS]
